import React from "react";
import { Routes, Route, BrowserRouter, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Intake from "@/pages/Intake";
import Invite from "@/pages/Invite";
import Aftercare from "@/pages/Aftercare";
import Consult from "@/pages/Consult";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCalendar from "@/pages/AdminCalendar";
import { registerServiceWorker } from "@/lib/push";

import "@/App.css";

if (typeof window !== "undefined") {
  // Fire and forget — register the SW so the app is installable + push-ready.
  registerServiceWorker();
}

/**
 * Embossed PD monogram backdrop — sits behind every page at the body level.
 * Single instance, off-screen bleed, very low opacity, soft-light blend.
 * Adds depth without ever reading as a visible logo.
 */
function BackdropMonogram() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
        overflow: "hidden",
      }}
    >
      <img
        src="/brand/monogram.webp"
        alt=""
        draggable={false}
        className="backdrop-monogram-img"
        style={{
          position: "absolute",
          top: "10%",
          right: "-25%",
          width: "140vw",
          maxWidth: "1800px",
          height: "auto",
          opacity: 0.05,
          mixBlendMode: "soft-light",
          filter: "blur(4px) drop-shadow(-3px -3px 0 rgba(255,255,255,0.20)) drop-shadow(3px 3px 6px rgba(0,0,0,0.6))",
        }}
      />
    </div>
  );
}

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A120E]">
        <div className="overline text-[#D2A853] animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BackdropMonogram />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/intake" element={<Intake />} />
            <Route path="/invite/:token" element={<Invite />} />
            <Route path="/aftercare" element={<Aftercare />} />
            <Route path="/consult" element={<Consult />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute role="client">
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendar"
              element={
                <ProtectedRoute role="admin">
                  <AdminCalendar />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
